import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { MatDialog } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import { AbcEmpresasCustomizablesComponent } from '@p-parametria/mantenimiento/abc-empresas/components/abc-empresas-customizables/abc-empresas-customizables.component';
import { ParametrosGeneralesService } from './services/parametros-generales.service';
import { IParametrosGeneralesRequest, IParametrosGenerales } from './interfaces/parametros-generales.interface';
import { MonedaService } from './services/moneda.service';
import { IMoneda } from './interfaces/moneda.interface';
import { PaisService } from '@p-parametria/portafolio/paises/services/pais.service';
import { IPais } from '@p-parametria/portafolio/paises/interfaces/pais.interface';
import { OpcionesService } from '@p-p-portafolio/services/opciones.service';
import { IDeptoCiu, IGetDepto_Ciudad } from '@p-p-portafolio/promociones/interfaces/IGetDepto_Ciudad';


@Component({
    selector: 'app-parametros-generales',
    templateUrl: './parametros-generales.component.html',
    providers: [MessageService]
})
export class ParametrosGeneralesComponent implements OnInit {
    form!: FormGroup;
    loading: boolean = false;
    tieneDatos: boolean = false; // Controla la visibilidad de botones
    paisCargadoDelBackend: string = ''; // Para guardar el país que viene del backend
    estadoCargadoDelBackend: string = ''; // Para guardar el estado que viene del backend

    // Opciones para los dropdowns
    estadosOpciones: { sOpcion: string; value: string }[] = [];
    paisesOpciones: { sOpcion: string; value: string }[] = [];
    monedasOpciones: { sOpcion: string; value: string }[] = [];
    paisesCompletos: IPais[] = []; // Para búsqueda por descripción
    estadosCompletos: IDeptoCiu[] = []; // Para búsqueda por nombre

    constructor(
        private fb: FormBuilder,
        private messageService: MessageService,
        private dialog: MatDialog,
        private parametrosGeneralesService: ParametrosGeneralesService,
        private monedaService: MonedaService,
        private paisService: PaisService,
        private opcionesService: OpcionesService
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.loadPaises();
        this.loadEstados();
        this.loadMonedas();
        this.loadData();
    }

    initForm(): void {
        this.form = this.fb.group({
            nombreCliente: [''],
            ciudad: [''],
            claveEstado: [''],
            pais: [''],
            nacionalidad: [''],
            moneda: [''],
            fechaSistema: [{ value: '', disabled: true }],
            haciendoCierre: [false]
        });
    }

    loadMonedas(): void {
        this.monedaService.getMonedas().subscribe({
            next: (response) => {
                if (response.success) {
                    // Mapear las monedas al formato requerido por el dropdown
                    this.monedasOpciones = response.data.map((moneda: IMoneda) => ({
                        sOpcion: `${moneda.moneda} - ${moneda.descripcion}`,
                        value: moneda.moneda
                    }));
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Advertencia',
                        detail: 'No se pudieron cargar las monedas'
                    });
                }
            },
            error: (error) => {
                console.error('Error al cargar monedas:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar las monedas'
                });
            }
        });
    }

    loadPaises(): void {
        this.paisService.getPaises().subscribe({
            next: (response) => {
                if (response.success) {
                    // Guardar los países completos para búsqueda
                    this.paisesCompletos = response.data;
                    // Mapear los países al formato requerido por el dropdown
                    this.paisesOpciones = response.data.map((pais: IPais) => ({
                        sOpcion: pais.descripcion,
                        value: pais.clave_Pais
                    }));
                    
                    // Si ya se cargó un país del backend, actualizar el formulario con la clave correcta
                    if (this.paisCargadoDelBackend) {
                        const clavePais = this.buscarClavePaisPorDescripcion(this.paisCargadoDelBackend);
                        this.form.patchValue({ pais: clavePais });
                    }
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Advertencia',
                        detail: 'No se pudieron cargar los países'
                    });
                }
            },
            error: (error) => {
                console.error('Error al cargar países:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar los países'
                });
            }
        });
    }

    /**
     * Busca la clave del país por su descripción (ignorando mayúsculas/minúsculas)
     * @param descripcionPais Descripción del país que viene del backend
     * @returns La clave del país o la descripción original si no se encuentra
     */
    private buscarClavePaisPorDescripcion(descripcionPais: string): string {
        if (!descripcionPais || this.paisesCompletos.length === 0) {
            return descripcionPais || '';
        }

        const paisEncontrado = this.paisesCompletos.find(
            (pais: IPais) => pais.descripcion.toLowerCase().trim() === descripcionPais.toLowerCase().trim()
        );

        return paisEncontrado ? paisEncontrado.clave_Pais : descripcionPais;
    }

    loadEstados(): void {
        const payload: IGetDepto_Ciudad = { identificador: 1, valor: "" };
        this.opcionesService.GetDeptoCiu(payload).subscribe({
            next: (response) => {
                if (response.success) {
                    // Guardar los estados completos para búsqueda
                    this.estadosCompletos = response.data;
                    // Mapear los estados al formato requerido por el dropdown
                    this.estadosOpciones = response.data.map((estado: IDeptoCiu) => ({
                        sOpcion: estado.nombre,
                        value: estado.id
                    }));
                    
                    // Si ya se cargó un estado del backend, actualizar el formulario con el ID correcto
                    if (this.estadoCargadoDelBackend) {
                        const idEstado = this.buscarIdEstadoPorNombre(this.estadoCargadoDelBackend);
                        this.form.patchValue({ claveEstado: idEstado });
                    }
                } else {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Advertencia',
                        detail: 'No se pudieron cargar los estados'
                    });
                }
            },
            error: (error) => {
                console.error('Error al cargar estados:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al cargar los estados'
                });
            }
        });
    }

    /**
     * Busca el ID del estado por su nombre (ignorando mayúsculas/minúsculas)
     * @param nombreEstado Nombre del estado que viene del backend
     * @returns El ID del estado o el nombre original si no se encuentra
     */
    private buscarIdEstadoPorNombre(nombreEstado: string): string {
        if (!nombreEstado || this.estadosCompletos.length === 0) {
            return nombreEstado || '';
        }

        const estadoEncontrado = this.estadosCompletos.find(
            (estado: IDeptoCiu) => estado.nombre.toLowerCase().trim() === nombreEstado.toLowerCase().trim()
        );

        return estadoEncontrado ? estadoEncontrado.id : nombreEstado;
    }

    loadData(): void {
        this.loading = true;

        // Primero verificar si existen registros
        const checkRequest: IParametrosGeneralesRequest = {
            que: 5,
            tope: "",
            numero_cliente: 0,
            siguientereporte: 0,
            nombre_cliente: "",
            ciudad: "",
            cve_estado: "",
            fechaSistema: 0,
            realizandoCierre: 0,
            pais: "",
            moneda: "",
            nacionalidad: "",
            rutaCriticaInicio: 0
        };

        this.parametrosGeneralesService.checkIfExists(checkRequest).subscribe({
            next: (checkResponse) => {
                if (checkResponse.success && checkResponse.data.length > 0) {
                    // Existen registros, cargar los datos
                    const loadRequest: IParametrosGeneralesRequest = {
                        que: 4,
                        tope: "",
                        numero_cliente: 1,
                        siguientereporte: 0,
                        nombre_cliente: "",
                        ciudad: "",
                        cve_estado: "",
                        fechaSistema: 0,
                        realizandoCierre: 0,
                        pais: "",
                        moneda: "",
                        nacionalidad: "",
                        rutaCriticaInicio: 0
                    };

                    this.parametrosGeneralesService.getParametrosGenerales(loadRequest).subscribe({
                        next: (response) => {
                            if (response.success && response.data.length > 0) {
                                const parametros: IParametrosGenerales = response.data[0];
                                
                                // Guardar el país y estado del backend para procesarlos después de cargar los datos
                                this.paisCargadoDelBackend = parametros.pais || '';
                                this.estadoCargadoDelBackend = parametros.cve_estado || '';
                                
                                // Verificar si realmente hay datos válidos (no todos vacíos)
                                const tieneDatosValidos = !!(parametros.nombre_cliente || 
                                                         parametros.ciudad || 
                                                         parametros.cve_estado || 
                                                         parametros.pais || 
                                                         parametros.nacionalidad || 
                                                         parametros.moneda || 
                                                         parametros.realizandoCierre === 1);
                                
                                this.tieneDatos = tieneDatosValidos; // Solo mostrar botones si hay datos válidos

                                // Convertir fechaSistema usando el servicio de conversión de fecha juliana
                                if (parametros.fechaSistema && parametros.fechaSistema > 0) {
                                    this.parametrosGeneralesService.convertirFechaJuliana(parametros.fechaSistema.toString()).subscribe({
                                        next: (fechaResponse) => {
                                            if (fechaResponse.success) {
                                                // Convertir fecha de dd/mm/yyyy a yyyy-mm-dd para el input date
                                                const fechaParts = fechaResponse.data.sFecha.split('/');
                                                const fechaFormateada = fechaParts.length === 3 
                                                    ? `${fechaParts[2]}-${fechaParts[1].padStart(2, '0')}-${fechaParts[0].padStart(2, '0')}`
                                                    : '';
                                                
                                                this.form.patchValue({
                                                    nombreCliente: parametros.nombre_cliente || '',
                                                    ciudad: parametros.ciudad || '',
                                                    claveEstado: this.buscarIdEstadoPorNombre(parametros.cve_estado || ''),
                                                    pais: this.buscarClavePaisPorDescripcion(parametros.pais || ''),
                                                    nacionalidad: parametros.nacionalidad || '',
                                                    moneda: parametros.moneda || '',
                                                    fechaSistema: fechaFormateada,
                                                    haciendoCierre: parametros.realizandoCierre === 1
                                                });
                                            } else {
                                                // Si falla la conversión, no cargar fecha
                                                this.form.patchValue({
                                                    nombreCliente: parametros.nombre_cliente || '',
                                                    ciudad: parametros.ciudad || '',
                                                    claveEstado: this.buscarIdEstadoPorNombre(parametros.cve_estado || ''),
                                                    pais: this.buscarClavePaisPorDescripcion(parametros.pais || ''),
                                                    nacionalidad: parametros.nacionalidad || '',
                                                    moneda: parametros.moneda || '',
                                                    fechaSistema: '',
                                                    haciendoCierre: parametros.realizandoCierre === 1
                                                });
                                            }
                                        },
                                        error: (error) => {
                                            console.error('Error al convertir fecha:', error);
                                            // Si falla la conversión, no cargar fecha
                                            this.form.patchValue({
                                                nombreCliente: parametros.nombre_cliente || '',
                                                ciudad: parametros.ciudad || '',
                                                claveEstado: this.buscarIdEstadoPorNombre(parametros.cve_estado || ''),
                                                pais: this.buscarClavePaisPorDescripcion(parametros.pais || ''),
                                                nacionalidad: parametros.nacionalidad || '',
                                                moneda: parametros.moneda || '',
                                                fechaSistema: '',
                                                haciendoCierre: parametros.realizandoCierre === 1
                                            });
                                        }
                                    });
                                } else {
                                    // Si fechaSistema es 0 o no existe, no cargar fecha (campo vacío)
                                    this.form.patchValue({
                                        nombreCliente: parametros.nombre_cliente || '',
                                        ciudad: parametros.ciudad || '',
                                        claveEstado: this.buscarIdEstadoPorNombre(parametros.cve_estado || ''),
                                        pais: this.buscarClavePaisPorDescripcion(parametros.pais || ''),
                                        nacionalidad: parametros.nacionalidad || '',
                                        moneda: parametros.moneda || '',
                                        fechaSistema: '',
                                        haciendoCierre: parametros.realizandoCierre === 1
                                    });
                                }
                            } else {
                                this.tieneDatos = false; // No hay datos, ocultar botones
                                this.messageService.add({
                                    severity: 'warn',
                                    summary: 'Advertencia',
                                    detail: 'No se encontraron parámetros generales'
                                });
                            }
                        },
                        error: (error) => {
                            this.tieneDatos = false; // Error al cargar, ocultar botones
                            console.error('Error al cargar parámetros generales:', error);
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Error al cargar los parámetros generales'
                            });
                        },
                        complete: () => {
                            this.loading = false;
                        }
                    });
                } else {
                    // No existen registros, ocultar botones
                    this.tieneDatos = false;
                    this.messageService.add({
                        severity: 'info',
                        summary: 'Información',
                        detail: 'No existen parámetros generales configurados'
                    });
                    this.loading = false;
                }
            },
            error: (error) => {
                this.tieneDatos = false; // Error al verificar, ocultar botones
                console.error('Error al verificar existencia de parámetros:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al verificar la existencia de parámetros generales'
                });
                this.loading = false;
            }
        });
    }

    aceptar(): void {
        if (this.form.invalid) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Por favor verifique los datos ingresados'
            });
            return;
        }

        this.loading = true;
        const formData = this.form.getRawValue();

        // Convertir fecha del formulario a formato juliano
        if (formData.fechaSistema) {
            // Convertir fecha de yyyy-mm-dd a dd/mm/yyyy para el servicio
            const fechaParts = formData.fechaSistema.split('-');
            const fechaFormateada = fechaParts.length === 3 
                ? `${fechaParts[2]}/${fechaParts[1]}/${fechaParts[0]}`
                : '';

            this.parametrosGeneralesService.convertirFechaAJuliana(fechaFormateada).subscribe({
                next: (fechaResponse) => {
                    if (fechaResponse.success) {
                        if (this.tieneDatos) {
                            this.actualizarParametros(formData, fechaResponse.data.juliana);
                        } else {
                            this.guardarParametros(formData, fechaResponse.data.juliana);
                        }
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Error al convertir la fecha'
                        });
                        this.loading = false;
                    }
                },
                error: (error) => {
                    console.error('Error al convertir fecha:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error al convertir la fecha'
                    });
                    this.loading = false;
                }
            });
        } else {
            // Si no hay fecha, usar fecha actual y convertirla
            const fechaActual = new Date();
            const fechaFormateada = `${fechaActual.getDate().toString().padStart(2, '0')}/${(fechaActual.getMonth() + 1).toString().padStart(2, '0')}/${fechaActual.getFullYear()}`;
            
            this.parametrosGeneralesService.convertirFechaAJuliana(fechaFormateada).subscribe({
                next: (fechaResponse) => {
                    if (fechaResponse.success) {
                        if (this.tieneDatos) {
                            this.actualizarParametros(formData, fechaResponse.data.juliana);
                        } else {
                            this.guardarParametros(formData, fechaResponse.data.juliana);
                        }
                    } else {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Error al convertir la fecha actual'
                        });
                        this.loading = false;
                    }
                },
                error: (error) => {
                    console.error('Error al convertir fecha actual:', error);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error al convertir la fecha actual'
                    });
                    this.loading = false;
                }
            });
        }
    }

    private guardarParametros(formData: any, fechaJuliana: number): void {
        const request: IParametrosGeneralesRequest = {
            que: 1,
            tope: "",
            numero_cliente: 1,
            siguientereporte: 0,
            nombre_cliente: formData.nombreCliente || "",
            ciudad: formData.ciudad || "",
            cve_estado: formData.claveEstado || "",
            fechaSistema: fechaJuliana,
            realizandoCierre: formData.haciendoCierre ? 1 : 0,
            pais: formData.pais || "",
            moneda: formData.moneda || "",
            nacionalidad: formData.nacionalidad || "",
            rutaCriticaInicio: 0
        };

        this.parametrosGeneralesService.saveParametrosGenerales(request).subscribe({
            next: (response) => {
                if (response.success && response.data === true) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Éxito',
                        detail: 'Parámetros guardados correctamente'
                    });
                    this.tieneDatos = true; // Mostrar botones después del guardado
                } else if (response.success && response.data === false) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo guardar. Verifique que todos los campos requeridos estén completos y que no exista un registro previo.'
                    });
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error al guardar los parámetros'
                    });
                }
            },
            error: (error) => {
                console.error('Error al guardar parámetros:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al guardar los parámetros'
                });
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    private actualizarParametros(formData: any, fechaJuliana: number): void {
        const request: IParametrosGeneralesRequest = {
            que: 2,
            tope: "",
            numero_cliente: 1,
            siguientereporte: 0,
            nombre_cliente: formData.nombreCliente || "",
            ciudad: formData.ciudad || "",
            cve_estado: formData.claveEstado || "",
            fechaSistema: fechaJuliana,
            realizandoCierre: formData.haciendoCierre ? 1 : 0,
            pais: formData.pais || "",
            moneda: formData.moneda || "",
            nacionalidad: formData.nacionalidad || "",
            rutaCriticaInicio: 0
        };

        this.parametrosGeneralesService.updateParametrosGenerales(request).subscribe({
            next: (response) => {
                if (response.success && response.data === true) {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Éxito',
                        detail: 'Parámetros actualizados correctamente'
                    });
                } else if (response.success && response.data === false) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No se pudo actualizar. Verifique que todos los campos requeridos estén completos.'
                    });
                } else {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error al actualizar los parámetros'
                    });
                }
            },
            error: (error) => {
                console.error('Error al actualizar parámetros:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Error al actualizar los parámetros'
                });
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    borrar(): void {
        Swal.fire({
            title: '¿Está seguro?',
            text: '¿Desea eliminar los parámetros generales?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            iconColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then((result) => {
            if (result.isConfirmed) {
                this.loading = true;

                const request: IParametrosGeneralesRequest = {
                    que: 3,
                    tope: "",
                    numero_cliente: 1,
                    siguientereporte: 0,
                    nombre_cliente: "",
                    ciudad: "",
                    cve_estado: "",
                    fechaSistema: 0,
                    realizandoCierre: 0,
                    pais: "",
                    moneda: "",
                    nacionalidad: "",
                    rutaCriticaInicio: 0
                };

                this.parametrosGeneralesService.deleteParametrosGenerales(request).subscribe({
                    next: (response) => {
                        if (response.success) {
                            this.messageService.add({
                                severity: 'success',
                                summary: 'Éxito',
                                detail: 'Parámetros eliminados correctamente'
                            });
                            this.tieneDatos = false; // Ocultar botones después del borrado
                            this.form.reset(); // Limpiar formulario
                        } else {
                            this.messageService.add({
                                severity: 'error',
                                summary: 'Error',
                                detail: 'Error al eliminar los parámetros'
                            });
                        }
                    },
                    error: (error) => {
                        console.error('Error al eliminar parámetros:', error);
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Error al eliminar los parámetros'
                        });
                    },
                    complete: () => {
                        this.loading = false;
                    }
                });
            }
        });
    }

    openCustomizables(): void {
        this.dialog.open(AbcEmpresasCustomizablesComponent, {
            width: '1000px',
            height: '1000px',
            disableClose: true,
            panelClass: ['custom-dialog-zindex'],
            hasBackdrop: true,
            autoFocus: false,
            data: {
                sTabla: 'param',
                sClave: 'general',
                sNombre: 'Parámetros Generales',
                sTipo: 'Opcion'
            }
        });
    }
} 